import { useEffect, useState } from 'react';

import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import KeyRoundedIcon from '@mui/icons-material/KeyRounded';
import PointOfSaleRoundedIcon from '@mui/icons-material/PointOfSaleRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { UseQueryResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { NormalizedApiError } from '@/shared/api';
import { getIntlLocale } from '@/shared/i18n';
import { CompactTablePagination, EmptyState, ErrorState, LoadingState } from '@/shared/ui';

import { newRetailEvotorIdempotencyKey } from '../../api/retailEvotorApi';
import {
  useCreateRetailEvotorAccount,
  useDeleteRetailEvotorAccounts,
  useToggleRetailEvotorAccount,
  useUpdateRetailEvotorAccount,
} from '../../hooks';
import type {
  RetailEvotorAccount,
  RetailEvotorAccountCreateRequest,
  RetailEvotorAccountUpdateRequest,
  RetailEvotorPage,
} from '../../model/settingsTypes';

import { RetailEvotorAccountFormDialog } from './RetailEvotorAccountFormDialog';
import { SettingsCard } from './SettingsCard';

function formatDate(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(getIntlLocale(), { dateStyle: 'medium' }).format(date);
}

function formatDateTime(value: string | null | undefined): string | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

export interface RetailEvotorAccountsPanelProps {
  query: UseQueryResult<RetailEvotorPage<RetailEvotorAccount>, NormalizedApiError>;
  filterInput: string;
  onFilterInputChange: (value: string) => void;
  page: number;
  size: number;
  onPageChange: (page: number) => void;
  onSizeChange: (size: number) => void;
  /** Every account mutation, including the sync toggle, is credential-gated (AC5). */
  canManageCredentials: boolean;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * Accounts tab — full CRUD + sync toggle over the tenant's Evotor accounts.
 *
 * Structurally modeled on `ProviderManager` (SettingsCard + header action + load/error/empty
 * states + delete-confirm dialog) but **not built on it**: `ProviderManager`'s columns (mode,
 * priority, per-row test-connection, `credential.present` chip) don't exist for Evotor accounts,
 * its list is unpaginated, and its edit semantics are "blank secret keeps the existing one" — the
 * exact opposite of this API's mandatory token re-entry.
 */
export function RetailEvotorAccountsPanel({
  query,
  filterInput,
  onFilterInputChange,
  page,
  size,
  onPageChange,
  onSizeChange,
  canManageCredentials,
  onSuccess,
  onError,
}: RetailEvotorAccountsPanelProps) {
  const { t } = useTranslation('settings');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editing, setEditing] = useState<RetailEvotorAccount | null>(null);
  const [createIdempotencyKey, setCreateIdempotencyKey] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<RetailEvotorAccount | null>(null);

  const createMutation = useCreateRetailEvotorAccount();
  const updateMutation = useUpdateRetailEvotorAccount();
  const deleteMutation = useDeleteRetailEvotorAccounts();
  const toggleMutation = useToggleRetailEvotorAccount();

  const openCreate = () => {
    setDialogMode('create');
    setEditing(null);
    setCreateIdempotencyKey(newRetailEvotorIdempotencyKey());
    setDialogOpen(true);
  };

  const openEdit = (account: RetailEvotorAccount) => {
    setDialogMode('edit');
    setEditing(account);
    setDialogOpen(true);
  };

  const handleSubmit = (
    body: RetailEvotorAccountCreateRequest | RetailEvotorAccountUpdateRequest,
  ) => {
    if (dialogMode === 'create') {
      createMutation.mutate(
        { body: body as RetailEvotorAccountCreateRequest, idempotencyKey: createIdempotencyKey },
        {
          onSuccess: (account) => {
            setDialogOpen(false);
            onSuccess(t('retailSettings.accounts.createdFeedback', { name: account.name }));
          },
          onError: (error) => onError(error.message),
        },
      );
    } else if (editing) {
      updateMutation.mutate(
        { evotorId: editing.evotorId, body: body as RetailEvotorAccountUpdateRequest },
        {
          onSuccess: (account) => {
            setDialogOpen(false);
            onSuccess(t('retailSettings.accounts.updatedFeedback', { name: account.name }));
          },
          onError: (error) => onError(error.message),
        },
      );
    }
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    deleteMutation.mutate(
      { idList: [target.id] },
      {
        onSuccess: () => {
          setDeleteTarget(null);
          onSuccess(t('retailSettings.accounts.deletedFeedback', { name: target.name }));
        },
        onError: (error) => onError(error.message),
      },
    );
  };

  const handleToggle = (account: RetailEvotorAccount) => {
    toggleMutation.mutate(
      { evotorId: account.evotorId, active: !account.active },
      {
        onSuccess: (message) => onSuccess(message),
        onError: (error) => onError(error.message),
      },
    );
  };

  const saving = createMutation.isPending || updateMutation.isPending;
  const data = query.data;
  const accounts = data?.content ?? [];
  const hasFilter = filterInput.trim() !== '';
  const showLinearProgress = query.isFetching && !query.isLoading;

  // If the current page becomes out of range (e.g. deleting the only account on the last page),
  // clamp back to the new last valid page instead of showing a misleading "no accounts yet" empty
  // state — with its own "connect an account" CTA — while accounts still exist on an earlier page.
  useEffect(() => {
    if (!data) return;
    const lastPage = Math.max(0, Math.ceil(data.page.totalElements / size) - 1);
    if (page > lastPage) {
      onPageChange(lastPage);
    }
  }, [data, page, size, onPageChange]);

  const action =
    canManageCredentials && data && accounts.length > 0 ? (
      <Button variant="outlined" size="small" startIcon={<AddRoundedIcon />} onClick={openCreate}>
        {t('retailSettings.accounts.addButton')}
      </Button>
    ) : undefined;

  return (
    <>
      {!canManageCredentials ? (
        <Alert severity="info" sx={{ mb: 2.5 }}>
          {t('retailSettings.credentialsRestrictedNotice')}
        </Alert>
      ) : null}

      <SettingsCard
        title={t('retailSettings.accounts.cardTitle')}
        description={t('retailSettings.accounts.cardDescription')}
        icon={<KeyRoundedIcon />}
        action={action}
        divided={false}
      >
        <Stack spacing={0} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pb: 1.5 }}>
            <TextField
              size="small"
              value={filterInput}
              onChange={(event) => onFilterInputChange(event.target.value)}
              placeholder={t('retailSettings.accounts.searchPlaceholder')}
              aria-label={t('retailSettings.accounts.searchPlaceholder')}
              sx={{ flexGrow: 1, maxWidth: 320 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Tooltip title={t('retailSettings.accounts.refreshAria')}>
              <IconButton
                aria-label={t('retailSettings.accounts.refreshAria')}
                onClick={() => void query.refetch()}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box sx={{ height: 4 }}>
            {showLinearProgress ? (
              <LinearProgress aria-label={t('retailSettings.refreshingAria')} sx={{ height: 4 }} />
            ) : null}
          </Box>

          {query.isLoading ? (
            <LoadingState label={t('scaffold.loading')} fullHeight={false} />
          ) : query.isError ? (
            <ErrorState
              title={t('retailSettings.accounts.loadError')}
              message={query.error?.message ?? t('common:error.unknown')}
              onRetry={() => void query.refetch()}
            />
          ) : accounts.length === 0 ? (
            hasFilter ? (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ py: 4, textAlign: 'center' }}
              >
                {t('retailSettings.accounts.noMatches', { filter: filterInput })}
              </Typography>
            ) : (
              <EmptyState
                icon={<PointOfSaleRoundedIcon sx={{ fontSize: 48 }} />}
                title={t('retailSettings.accounts.emptyTitle')}
                description={
                  canManageCredentials
                    ? t('retailSettings.accounts.emptyDescription')
                    : t('retailSettings.accounts.emptyDescriptionNoAccess')
                }
                action={
                  canManageCredentials ? (
                    <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={openCreate}>
                      {t('retailSettings.accounts.addButton')}
                    </Button>
                  ) : undefined
                }
              />
            )
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('retailSettings.accounts.table.name')}</TableCell>
                      <TableCell>{t('retailSettings.accounts.table.evotorId')}</TableCell>
                      <TableCell>{t('retailSettings.accounts.table.sync')}</TableCell>
                      <TableCell>{t('retailSettings.accounts.table.updated')}</TableCell>
                      <TableCell align="right">
                        {t('retailSettings.accounts.table.actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {accounts.map((account) => {
                      const createdAt = formatDate(account.createdAt);
                      const updatedAt = formatDateTime(account.updatedAt);
                      const rowBusy =
                        toggleMutation.isPending &&
                        toggleMutation.variables?.evotorId === account.evotorId;
                      const toggleDisabled = !canManageCredentials || rowBusy;
                      const toggleControl = (
                        <Switch
                          size="small"
                          checked={account.active}
                          disabled={toggleDisabled}
                          onChange={() => handleToggle(account)}
                          slotProps={{
                            input: {
                              'aria-label': t('retailSettings.accounts.syncToggleAria', {
                                name: account.name,
                              }),
                            },
                          }}
                        />
                      );
                      return (
                        <TableRow key={account.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight={600}>
                              {account.name}
                            </Typography>
                            {createdAt ? (
                              <Typography variant="caption" color="text.secondary">
                                {createdAt}
                              </Typography>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              sx={{ fontFamily: 'JetBrains Mono, monospace' }}
                            >
                              {account.evotorId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            {!canManageCredentials ? (
                              <Tooltip title={t('retailSettings.noPermissionTooltip')}>
                                <span>{toggleControl}</span>
                              </Tooltip>
                            ) : (
                              toggleControl
                            )}
                          </TableCell>
                          <TableCell>{updatedAt ?? '—'}</TableCell>
                          <TableCell align="right">
                            <Tooltip
                              title={
                                canManageCredentials
                                  ? t('retailSettings.accounts.editTooltip')
                                  : t('retailSettings.noPermissionTooltip')
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  aria-label={t('retailSettings.accounts.editAria', {
                                    name: account.name,
                                  })}
                                  disabled={!canManageCredentials}
                                  onClick={() => openEdit(account)}
                                >
                                  <EditRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip
                              title={
                                canManageCredentials
                                  ? t('common:action.delete')
                                  : t('retailSettings.noPermissionTooltip')
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  aria-label={t('retailSettings.accounts.deleteAria', {
                                    name: account.name,
                                  })}
                                  disabled={!canManageCredentials}
                                  onClick={() => setDeleteTarget(account)}
                                >
                                  <DeleteOutlineRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <CompactTablePagination
                count={data?.page.totalElements ?? 0}
                page={page}
                rowsPerPage={size}
                rowsPerPageOptions={[10, 20, 50]}
                onPageChange={(_event, next) => onPageChange(next)}
                onRowsPerPageChange={(event) => {
                  onSizeChange(parseInt(event.target.value, 10));
                  onPageChange(0);
                }}
              />
            </>
          )}
        </Stack>
      </SettingsCard>

      <RetailEvotorAccountFormDialog
        open={dialogOpen}
        mode={dialogMode}
        account={editing}
        saving={saving}
        onClose={() => setDialogOpen(false)}
        onSubmit={handleSubmit}
      />

      <Dialog open={deleteTarget !== null} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>{t('retailSettings.accountDelete.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('retailSettings.accountDelete.body', { name: deleteTarget?.name })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>
            {t('common:action.cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDelete}
            disabled={deleteMutation.isPending}
            startIcon={
              deleteMutation.isPending ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteOutlineRoundedIcon />
              )
            }
          >
            {t('retailSettings.accountDelete.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
